import {Component, EventEmitter, Input, Output} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FingerprintModalComponent} from '../fingerprint-modal/fingerprint-modal.component';
import {IconProp, LangService} from 'ngx-satoris';

interface Finger {
  nfiq: number;
  data: string;
  previewData?: string;
}

interface Hand {
  Index?: Finger;
  Middle?: Finger;
  Ring?: Finger;
  Little?: Finger;
}

export interface Fingerprints {
  /**
   * Level    PPI     PxDepth     DR      Certif.
   * 10       125     1           2       /
   * 20       250     3           5       /
   * 30       500     8           80      /
   * 31       500     8           200     EFTS/F
   * 40       1000    8           120     /
   * 41       1000    8           200     EFTS/F
   */
  ImageAcquisitionLevel: 10 | 20 | 30 | 31 | 40 | 41;
  LeftHand: Hand;
  Thumbs: {
    Left?: Finger;
    Right?: Finger;
  }
  RightHand: Hand;
}

const RS_FGP_RIGHT_THUMB = 1;
const RS_FGP_RIGHT_INDEX = 2;
const RS_FGP_RIGHT_MIDDLE = 3;
const RS_FGP_RIGHT_RING = 4;
const RS_FGP_RIGHT_LITTLE = 5;
const RS_FGP_LEFT_THUMB = 6;
const RS_FGP_LEFT_INDEX = 7;
const RS_FGP_LEFT_MIDDLE = 8;
const RS_FGP_LEFT_RING = 9;
const RS_FGP_LEFT_LITTLE = 10;

const encode = (u8arr: Uint8Array) => btoa(String.fromCharCode(...u8arr));

@Component({
  selector: 'app-fingerprint-input',
  templateUrl: './fingerprint-input.component.html',
  styleUrl: './fingerprint-input.component.scss'
})
export class FingerprintInputComponent {
  @Input() icon: IconProp;
  @Input() labelKey: string;
  @Input() description: string = '';
  @Input() hasValue: boolean = false;
  @Input() value: Fingerprints | undefined;
  @Output() captured = new EventEmitter<Fingerprints>();

  displayPreview: boolean = false;

  constructor(public dialog: MatDialog,
    private lang: LangService) { }

  toggleDisplayPreview() {
    this.displayPreview = !this.displayPreview;
  }

  private static toBase64(previewData: {
    width: number;
    height: number;
    data: Uint8Array;
  }): string {
    const canvas = document.createElement('canvas');
    canvas.width = previewData.width;
    canvas.height = previewData.height;
    const ctx = canvas.getContext('2d');
    if(!ctx) return '';
    const imageData = ctx.createImageData(previewData.width, previewData.height);
    for(let i = 0; i < previewData.data.length; i++) {
      const gray = previewData.data[i];
      imageData.data[i * 4 + 0] = gray; // R
      imageData.data[i * 4 + 1] = gray; // G
      imageData.data[i * 4 + 2] = gray; // B
      imageData.data[i * 4 + 3] = 255;  // A
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  }

  renderNfiq(nfiq: number): string {
    if(nfiq === 0) {
      return this.lang.transform('finger.missing');
    }
    return `${20 * (6 - nfiq)}%`;
  }

  openFingerprintModal(): void {
    const dialogRef = this.dialog.open(FingerprintModalComponent);
    dialogRef.afterClosed().subscribe((result: {
      [id: number]: {
        nfiq: number,
        data: Uint8Array,
        previewData: {
          width: number,
          height: number,
          data: Uint8Array
        }
      }
    }) => {
      this.captured.emit({
        ImageAcquisitionLevel: electron.addon.getFingerprintImageAcquisitionLevel(),
        LeftHand: {
          Index: {
            nfiq: result[RS_FGP_LEFT_INDEX]?.nfiq || 0,
            data: result[RS_FGP_LEFT_INDEX] ? encode(result[RS_FGP_LEFT_INDEX].data) : '',
            previewData: result[RS_FGP_LEFT_INDEX] ? FingerprintInputComponent.toBase64(result[RS_FGP_LEFT_INDEX].previewData) : undefined
          },
          Middle: {
            nfiq: result[RS_FGP_LEFT_MIDDLE]?.nfiq || 0,
            data: result[RS_FGP_LEFT_MIDDLE] ? encode(result[RS_FGP_LEFT_MIDDLE].data) : '',
            previewData: result[RS_FGP_LEFT_MIDDLE] ? FingerprintInputComponent.toBase64(result[RS_FGP_LEFT_MIDDLE].previewData) : undefined
          },
          Ring: {
            nfiq: result[RS_FGP_LEFT_RING]?.nfiq || 0,
            data: result[RS_FGP_LEFT_RING] ? encode(result[RS_FGP_LEFT_RING].data) : '',
            previewData: result[RS_FGP_LEFT_RING] ? FingerprintInputComponent.toBase64(result[RS_FGP_LEFT_RING].previewData) : undefined
          },
          Little: {
            nfiq: result[RS_FGP_LEFT_LITTLE]?.nfiq || 0,
            data: result[RS_FGP_LEFT_LITTLE] ? encode(result[RS_FGP_LEFT_LITTLE].data) : '',
            previewData: result[RS_FGP_LEFT_LITTLE] ? FingerprintInputComponent.toBase64(result[RS_FGP_LEFT_LITTLE].previewData) : undefined
          }
        },
        Thumbs: {
          Left: {
            nfiq: result[RS_FGP_LEFT_THUMB]?.nfiq || 0,
            data: result[RS_FGP_LEFT_THUMB] ? encode(result[RS_FGP_LEFT_THUMB].data) : '',
            previewData: result[RS_FGP_LEFT_THUMB] ? FingerprintInputComponent.toBase64(result[RS_FGP_LEFT_THUMB].previewData) : undefined
          },
          Right: {
            nfiq: result[RS_FGP_RIGHT_THUMB]?.nfiq || 0,
            data: result[RS_FGP_RIGHT_THUMB] ? encode(result[RS_FGP_RIGHT_THUMB].data) : '',
            previewData: result[RS_FGP_RIGHT_THUMB] ? FingerprintInputComponent.toBase64(result[RS_FGP_RIGHT_THUMB].previewData) : undefined
          }
        },
        RightHand: {
          Index: {
            nfiq: result[RS_FGP_RIGHT_INDEX]?.nfiq || 0,
            data: result[RS_FGP_RIGHT_INDEX] ? encode(result[RS_FGP_RIGHT_INDEX].data) : '',
            previewData: result[RS_FGP_RIGHT_INDEX] ? FingerprintInputComponent.toBase64(result[RS_FGP_RIGHT_INDEX].previewData) : undefined

          },
          Middle: {
            nfiq: result[RS_FGP_RIGHT_MIDDLE]?.nfiq || 0,
            data: result[RS_FGP_RIGHT_MIDDLE] ? encode(result[RS_FGP_RIGHT_MIDDLE].data) : '',
            previewData: result[RS_FGP_RIGHT_MIDDLE] ? FingerprintInputComponent.toBase64(result[RS_FGP_RIGHT_MIDDLE].previewData) : undefined
          },
          Ring: {
            nfiq: result[RS_FGP_RIGHT_RING]?.nfiq || 0,
            data: result[RS_FGP_RIGHT_RING] ? encode(result[RS_FGP_RIGHT_RING].data) : '',
            previewData: result[RS_FGP_RIGHT_RING] ? FingerprintInputComponent.toBase64(result[RS_FGP_RIGHT_RING].previewData) : undefined
          },
          Little: {
            nfiq: result[RS_FGP_RIGHT_LITTLE]?.nfiq || 0,
            data: result[RS_FGP_RIGHT_LITTLE] ? encode(result[RS_FGP_RIGHT_LITTLE].data) : '',
            previewData: result[RS_FGP_RIGHT_LITTLE] ? FingerprintInputComponent.toBase64(result[RS_FGP_RIGHT_LITTLE].previewData) : undefined
          }
        }
      });
    });
  }
}
